import { supabase } from '../lib/supabase';
import { logAuditEvent } from './auditLogService';

/**
 * Send an optional tip from a client to an expert on a completed contract.
 *
 * @param {string}  contractId        — UUID of the completed contract
 * @param {number}  amount            — USD amount (min $1)
 * @param {string}  [paymentMethodId] — Stripe PM id; defaults to user's saved default card
 * @param {string}  [message]         — Optional thank-you note
 */
export async function sendTip({ contractId, amount, paymentMethodId, message }) {
  try {
    const body = { contractId, amount };
    if (paymentMethodId) body.paymentMethodId = paymentMethodId;
    if (message?.trim()) body.message = message.trim();

    const { data, error } = await supabase?.functions?.invoke('tip-expert', { body });

    if (error) {
      // Extract the real message from the edge function response
      let msg = error?.message || 'Unknown error';
      try {
        const body = await error?.context?.json?.();
        if (body?.error) msg = body.error;
      } catch (_) { /* ignore parse errors */ }
      throw new Error(msg);
    }

    if (data?.error) throw new Error(data.error);

    logAuditEvent({
      actionType:        'payment.tip_sent',
      actionDescription: `Client sent a $${amount.toFixed(2)} tip`,
      entityType:        'contract',
      entityId:          contractId,
      metadata:          { amount },
      severity:          'info',
    }).catch(console.error);

    return { success: true, tipId: data?.tipId, amount };
  } catch (error) {
    console.error('Send tip error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Returns true if the current user has already sent a tip on this contract.
 */
export async function hasTippedContract(contractId) {
  try {
    const { data } = await supabase
      ?.from('tips')
      ?.select('id')
      ?.eq('contract_id', contractId)
      ?.eq('status', 'succeeded')
      ?.limit(1)
      ?.maybeSingle();
    return !!data?.id;
  } catch {
    return false;
  }
}

/**
 * Fetch tips a client has sent on a contract (to show history / prevent accidental re-tips).
 */
export async function getTipsForContract(contractId) {
  try {
    const { data, error } = await supabase
      ?.from('tips')
      ?.select('id, amount, message, status, created_at')
      ?.eq('contract_id', contractId)
      ?.eq('status', 'succeeded')
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, tips: data || [] };
  } catch (error) {
    console.error('Get tips error:', error);
    return { success: false, tips: [], error: error?.message };
  }
}

/**
 * Fetch the user's saved payment methods (for the PM picker in TipModal).
 */
export async function getSavedPaymentMethods() {
  try {
    const { data, error } = await supabase
      ?.from('user_payment_methods')
      ?.select('stripe_payment_method_id, card_brand, card_last4, card_exp_month, card_exp_year, is_default')
      ?.order('is_default', { ascending: false });

    if (error) throw error;
    return { success: true, methods: data || [] };
  } catch (error) {
    console.error('Get payment methods error:', error);
    return { success: false, methods: [], error: error?.message };
  }
}
